// src/pages/WalletPage.jsx - Page portefeuille & historique
import { useEffect, useState } from 'react';
import { usePaymentStore, useAuthStore } from '../store';
import api from '../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import RatesDisplay from '../components/RatesDisplay';

const C = {
  bg: '#0A0E1A', surface: '#111827', card: '#1a2235',
  border: '#1e2d45', accent: '#00D4AA', gold: '#F5C542',
  red: '#FF4F6A', text: '#E8EDF5', muted: '#6b7fa3',
};

export function WalletPage() {
  const { wallet, transactions, fetchWallet, fetchTransactions, isLoading } = usePaymentStore();
  const { user } = useAuthStore();
  const [filter, setFilter] = useState('all');
  const [showOMModal, setShowOMModal] = useState(false);
  const [omPhone, setOmPhone] = useState('');
  const [omAmount, setOmAmount] = useState('');
  const [omStep, setOmStep] = useState('form'); // form, pending, success, failed
  const [omPayToken, setOmPayToken] = useState('');
  const [omError, setOmError] = useState('');
  const [omLoading, setOmLoading] = useState(false);
  const [multiWallets, setMultiWallets] = useState([]);
  const [viewCurrency, setViewCurrency] = useState('XAF'); // XAF | EUR | USD
  const [convertedBalance, setConvertedBalance] = useState(null);

  useEffect(() => {
    fetchWallet();
    fetchTransactions();
    api.get('/wallet/balances').then(r => {
      if (r.data?.data?.multi) setMultiWallets(r.data.data.multi);
      if (r.data?.data?.primary) {
        const p = r.data.data.primary;
        setConvertedBalance({ eur: p.eur_equivalent, usd: p.usd_equivalent });
      }
    }).catch(() => {});
  }, []);

  const filtered = transactions.filter(tx => {
    if (filter === 'sent') return tx.sender_id === user?.id;
    if (filter === 'received') return tx.receiver_id === user?.id;
    return true;
  });

  const fmt = (n) => parseFloat(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleOMPayment = async () => {
    if (!omPhone || !omAmount) return;
    setOmLoading(true);
    setOmError('');
    try {
      const initRes = await api.post('/om/init', { amount: omAmount, subscriberMsisdn: omPhone, description: 'Recharge PayChat' });
      const payToken = initRes.payToken;
      setOmPayToken(payToken);
      await api.post('/om/pay', { payToken, subscriberMsisdn: omPhone, amount: omAmount, description: 'Recharge PayChat' });
      setOmStep('pending');
      // Polling statut
      const interval = setInterval(async () => {
        try {
          const statusRes = await api.get(`/om/status/${payToken}`);
          if (statusRes.status === 'SUCCESSFULL' || statusRes.status === 'SUCCESS') {
            clearInterval(interval);
            setOmStep('success');
            fetchWallet();
          } else if (statusRes.status === 'FAILED' || statusRes.status === 'EXPIRED') {
            clearInterval(interval);
            setOmStep('failed');
            setOmError('Transaction echouee ou expiree.');
          }
        } catch(e) {}
      }, 5000);
      setTimeout(() => clearInterval(interval), 120000);
    } catch(err) {
      setOmError(err?.error || 'Erreur lors du paiement');
      setOmStep('form');
    } finally {
      setOmLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'Sora', sans-serif", color: C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');`}</style>

      {/* Header */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to="/" style={{ color: C.muted, textDecoration: 'none', fontSize: 20 }}>←</Link>
        <h1 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>Mon Portefeuille</h1>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px' }}>

        {/* Carte solde */}
        <div style={{
          background: 'linear-gradient(135deg, #003d2e, #001a14)',
          border: `1px solid ${C.accent}44`,
          borderRadius: 20, padding: '28px 28px',
          marginBottom: 24, boxShadow: `0 0 40px #00d4aa22`,
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ fontSize: 11, color: C.accent, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
            Solde disponible
          </div>
          <div style={{ fontSize: 48, fontWeight: 800, letterSpacing: '-2px', lineHeight: 1 }}>
            FCFA {wallet ? fmt(wallet.wallet?.balance) : '—'}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>{user?.display_name} · PayChat Wallet</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button onClick={()=>setShowOMModal(true)} style={{ padding: '8px 16px', background: 'linear-gradient(90deg,#FF6600,#FF8C00)', border: 'none', borderRadius: 10, cursor: 'pointer', color: '#fff', fontSize: 12, fontWeight: 700 }}>
              🟠 Recharger OM
            </button>
          </div>

          {/* Affichage multidevise */}
          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { code: 'XAF', label: 'FCFA', value: wallet?.wallet?.balance },
              { code: 'EUR', label: 'EUR', value: convertedBalance?.eur },
              { code: 'USD', label: 'USD', value: convertedBalance?.usd },
            ].map(({ code, label, value }) => (
              value != null && (
                <button key={code} onClick={() => setViewCurrency(code)} style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                  cursor: 'pointer', border: 'none',
                  background: viewCurrency === code ? '#00D4AA' : '#0A0E1A22',
                  color: viewCurrency === code ? '#000' : '#6b7fa3',
                }}>
                  {code === 'XAF' ? `FCFA ${code}` : code}
                </button>
              )
            ))}
          </div>
          {wallet?.stats && (
            <div style={{ display: 'flex', gap: 24, marginTop: 24, paddingTop: 20, borderTop: `1px solid ${C.accent}22` }}>
              {[
                { label: 'Envoyé', value: `FCFA ${fmt(wallet.stats.total_sent)}`, color: C.red },
                { label: 'Reçu', value: `FCFA ${fmt(wallet.stats.total_received)}`, color: C.accent },
                { label: 'Transactions', value: wallet.stats.total_transactions, color: C.gold },
              ].map(({ label, value, color }) => (
                <div key={label}>
                  <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Taux de change */}
        <RatesDisplay compact={true} />

        {multiWallets.length > 0 && (
          <div style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 14, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#E8EDF5', marginBottom: 10 }}>Autres wallets</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {multiWallets.map(w => (
                <div key={w.id} style={{ background: '#0A0E1A', borderRadius: 10, padding: '8px 14px', border: '1px solid #1e2d45' }}>
                  <div style={{ fontSize: 10, color: '#6b7fa3', marginBottom: 2 }}>{w.currency}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: w.is_frozen ? '#FF4F6A' : '#00D4AA' }}>
                    {parseFloat(w.balance).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                    {w.is_frozen && <span style={{ fontSize: 10, marginLeft: 4 }}>locked</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filtres */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[['all', 'Tout'], ['sent', '↗ Envoyé'], ['received', '↙ Reçu']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)} style={{
              padding: '8px 16px', borderRadius: 10,
              background: filter === val ? `${C.accent}22` : C.card,
              border: `1px solid ${filter === val ? C.accent : C.border}`,
              color: filter === val ? C.accent : C.muted,
              fontWeight: filter === val ? 700 : 400,
              fontSize: 13, cursor: 'pointer', fontFamily: "'Sora', sans-serif",
            }}>{label}</button>
          ))}
        </div>

        {/* Historique */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {isLoading && <div style={{ textAlign: 'center', color: C.muted, padding: 40 }}>Chargement...</div>}
          {!isLoading && filtered.length === 0 && (
            <div style={{ textAlign: 'center', color: C.muted, padding: 60 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>💸</div>
              <div>Aucune transaction</div>
            </div>
          )}
          {filtered.map(tx => {
            const isSent = tx.sender_id === user?.id;
            return (
              <div key={tx.id} style={{
                background: C.card, border: `1px solid ${C.border}`,
                borderRadius: 14, padding: '14px 18px',
                display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: isSent ? '#ff4f6a15' : '#00d4aa15',
                  border: `1px solid ${isSent ? C.red + '44' : C.accent + '44'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, flexShrink: 0,
                }}>
                  {isSent ? '↗' : '↙'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {isSent ? tx.receiver_name : tx.sender_name}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    {tx.note || tx.type} · {tx.reference}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                    {tx.created_at && format(new Date(tx.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: isSent ? C.red : C.accent }}>
                    {isSent ? '-' : '+'}FCFA {fmt(isSent ? tx.amount : tx.net_amount)}
                  </div>
                  <div style={{
                    fontSize: 10, marginTop: 4, padding: '2px 8px', borderRadius: 6,
                    background: tx.status === 'completed' ? '#00d4aa22' : tx.status === 'pending' ? '#f5c54222' : '#ff4f6a22',
                    color: tx.status === 'completed' ? C.accent : tx.status === 'pending' ? C.gold : C.red,
                    fontWeight: 600,
                  }}>
                    {tx.status === 'completed' ? '✓ Complété' : tx.status === 'pending' ? '⏳ En attente' : '✗ Échoué'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal Orange Money */}
      {showOMModal && (
        <div style={{position:'fixed',inset:0,background:'#000000CC',display:'flex',alignItems:'center',justifyContent:'center',zIndex:500,backdropFilter:'blur(8px)'}}>
          <div style={{background:'#111827',border:'1px solid #1e2d45',borderRadius:24,padding:28,width:'90%',maxWidth:360,textAlign:'center'}}>
            <div style={{fontSize:32,marginBottom:8}}>🟠</div>
            <div style={{fontSize:18,fontWeight:800,color:'#E8EDF5',marginBottom:4}}>Recharge Orange Money</div>

            {omStep === 'form' && (
              <>
                <div style={{fontSize:13,color:'#6b7fa3',marginBottom:20}}>Entrez votre numero OM et le montant</div>
                <input
                  value={omPhone}
                  onChange={e=>setOmPhone(e.target.value)}
                  placeholder="Numero Orange Money (ex: 699123456)"
                  style={{width:'100%',padding:'12px',background:'#1a2235',border:'1px solid #1e2d45',borderRadius:12,color:'#E8EDF5',fontSize:14,marginBottom:12,boxSizing:'border-box',outline:'none'}}
                />
                <input
                  value={omAmount}
                  onChange={e=>setOmAmount(e.target.value)}
                  placeholder="Montant en FCFA"
                  type="number"
                  style={{width:'100%',padding:'12px',background:'#1a2235',border:'1px solid #1e2d45',borderRadius:12,color:'#E8EDF5',fontSize:14,marginBottom:16,boxSizing:'border-box',outline:'none'}}
                />
                {omError && <div style={{color:'#FF4F6A',fontSize:13,marginBottom:12}}>{omError}</div>}
                <div style={{display:'flex',gap:12}}>
                  <button onClick={()=>{setShowOMModal(false);setOmStep('form');setOmError('');}} style={{flex:1,padding:'12px',background:'#1a2235',border:'1px solid #1e2d45',borderRadius:12,cursor:'pointer',color:'#6b7fa3',fontSize:14,fontWeight:600}}>Annuler</button>
                  <button onClick={handleOMPayment} disabled={omLoading||!omPhone||!omAmount} style={{flex:2,padding:'12px',background:omLoading||!omPhone||!omAmount?'#2a3a4a':'linear-gradient(90deg,#FF6600,#FF8C00)',border:'none',borderRadius:12,cursor:'pointer',color:'#fff',fontSize:14,fontWeight:700}}>
                    {omLoading ? 'Traitement...' : 'Payer'}
                  </button>
                </div>
              </>
            )}

            {omStep === 'pending' && (
              <>
                <div style={{fontSize:13,color:'#6b7fa3',marginBottom:20}}>Un message a ete envoye sur votre telephone.<br/>Confirmez le paiement avec votre PIN Orange Money.</div>
                <div style={{color:'#F59E0B',fontSize:14,fontWeight:700,marginBottom:20}}>⏳ En attente de confirmation...</div>
                <button onClick={()=>{setShowOMModal(false);setOmStep('form');}} style={{padding:'12px 24px',background:'#1a2235',border:'1px solid #1e2d45',borderRadius:12,cursor:'pointer',color:'#6b7fa3',fontSize:14}}>Fermer</button>
              </>
            )}

            {omStep === 'success' && (
              <>
                <div style={{fontSize:48,margin:'16px 0'}}>✅</div>
                <div style={{color:'#00D4AA',fontSize:16,fontWeight:700,marginBottom:20}}>Recharge reussie !</div>
                <button onClick={()=>{setShowOMModal(false);setOmStep('form');}} style={{padding:'12px 24px',background:'linear-gradient(90deg,#00D4AA,#00b894)',border:'none',borderRadius:12,cursor:'pointer',color:'#000',fontSize:14,fontWeight:700}}>Fermer</button>
              </>
            )}

            {omStep === 'failed' && (
              <>
                <div style={{fontSize:48,margin:'16px 0'}}>❌</div>
                <div style={{color:'#FF4F6A',fontSize:14,marginBottom:20}}>{omError}</div>
                <div style={{display:'flex',gap:12}}>
                  <button onClick={()=>{setShowOMModal(false);setOmStep('form');setOmError('');}} style={{flex:1,padding:'12px',background:'#1a2235',border:'1px solid #1e2d45',borderRadius:12,cursor:'pointer',color:'#6b7fa3',fontSize:14}}>Fermer</button>
                  <button onClick={()=>{setOmStep('form');setOmError('');}} style={{flex:1,padding:'12px',background:'#FF4F6A',border:'none',borderRadius:12,cursor:'pointer',color:'#fff',fontSize:14,fontWeight:700}}>Reessayer</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
